import React, { Component } from 'react';
import type { Store } from 'redux';
import noop from 'lodash/noop';
import AppBase from '@atlassian/jira-app-base';
import AppStyle from '@atlassian/jira-common-components-app-style';
import { ExperienceTracker as ViewExperienceTracker } from '@atlassian/jira-common-experience-tracking-viewing/src/view/experience-tracker-consumer/tracker-base/index.tsx';
import { ViewExperienceTrackingProvider } from '@atlassian/jira-common-experience-tracking-viewing/src/view/experience-tracking-provider/index.tsx';
import isShallowEqual from '@atlassian/jira-common-util-is-shallow-equal';
import { SetBundleReadyMark } from '@atlassian/jira-providers-spa-apdex-analytics';
import { JIRA_SOFTWARE } from '@atlassian/jira-shared-types/src/application.tsx';
import Spinner from '@atlassian/jira-software-report-charting-common/src/ui/spinner/index.tsx';
import ReportPage from '@atlassian/jira-software-report-page';
import { REPORT_VELOCITY } from '@atlassian/jira-software-report-page/src/common/constants.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { isPollinatorTenant } from './feature-flags';
import {
	APP_ID,
	SLA_EXPERIENCE,
	SLA_FAILURE_REASON_REDUX_ERROR,
	SLA_LOCATION,
} from './model/constants';
import type { AppProps as Props } from './model/types';
import rootEpic from './ops';
import createStore from './state';
import { initializeAppProps } from './state/actions/app';
import type { State } from './state/types';
import View from './view';

// eslint-disable-next-line jira/react/no-class-components
export class VelocityReport extends Component<Props> {
	static defaultProps = {
		onExperienceSuccess: noop,
		onExperienceFailure: noop,
	};

	constructor(props: Props) {
		super(props);

		this.store = createStore(rootEpic, this.onError);
		this.store.dispatch(initializeAppProps(this.props));
	}

	componentDidUpdate(prevProps: Props) {
		if (isShallowEqual(prevProps, this.props)) {
			return;
		}
		this.store.dispatch(initializeAppProps(this.props));
	}

	onError = () => {
		this.props.onExperienceFailure('', {
			failureReason: SLA_FAILURE_REASON_REDUX_ERROR,
		});
	};

	store: Store<State>;

	render() {
		const { onExperienceFailure, onExperienceSuccess } = this.props;

		return (
			<AppBase
				id={APP_ID}
				store={this.store}
				teamName="helios-reports"
				packageName="@atlassian/jira-software-velocity-report"
			>
				<AppStyle>
					{__SPA__ && <SetBundleReadyMark />}
					{__SERVER__ ? (
						<Spinner />
					) : (
						<View
							onExperienceSuccess={onExperienceSuccess}
							onExperienceFailure={onExperienceFailure}
						/>
					)}
				</AppStyle>
			</AppBase>
		);
	}
}

type AppProps = {
	readonly baseUrl: string;
	readonly boardId: number;
	readonly projectKey: string;
};

const App = (props: AppProps) => {
	const tenantContext = useTenantContext();
	const { baseUrl, projectKey, boardId } = props;

	return (
		<ViewExperienceTrackingProvider
			experience={SLA_EXPERIENCE}
			analyticsSource={REPORT_VELOCITY}
			application={JIRA_SOFTWARE}
			edition={tenantContext.appEditions.software}
			additionalAttributes={{
				synthetic: isPollinatorTenant(),
			}}
		>
			<ViewExperienceTracker location={SLA_LOCATION}>
				{({ onExperienceSuccess, onExperienceFailure }) => (
					<ReportPage
						baseUrl={baseUrl}
						projectKey={projectKey}
						boardId={boardId}
						reportType={REPORT_VELOCITY}
					>
						<VelocityReport
							baseUrl={baseUrl}
							boardId={boardId}
							onExperienceSuccess={onExperienceSuccess}
							onExperienceFailure={onExperienceFailure}
						/>
					</ReportPage>
				)}
			</ViewExperienceTracker>
		</ViewExperienceTrackingProvider>
	);
};

export default App;
